import React, { useEffect, useState } from "react"
import styled from "styled-components"
import Header from "components/header/header"
import { createHierarchicalMenusFromAllWpMenuNodes } from "@bufox/gatsby-utils/fe/wp"
import Footer from "components/footer/footer"
import PropTypes from "prop-types"
import themeConfig from "utils/theme-config"
import "utils/fonts"
import DesktopSkin from "components/ad-slots/desktop-skin"
import ManageConsent from "@bufox/gatsby-utils/fe/google-services/ad-manager/manage-consent"
import { DFPSlotsProvider, DFPManager } from "react-dfp"
import {
  gamActive,
  gamIsJustConsent,
  gamNetworkCode,
} from "@bufox/gatsby-utils/fe/google-services/ad-manager/utils"
import StickyAdSlot from "components/ad-slots/sticky"
const { screens } = themeConfig
const Layout = props => {
  const {
    children,
    className,
    generalSettings,
    homepageUri,
    footer,
    language,
    location,
    rawMenus,
    searchSettings,
    searchUrls,
    socialNetworks,
    translations,
    wpSettings,
  } = props
  const siteTitle = generalSettings?.siteTitle
    ? generalSettings.siteTitle
    : "Site Title"
  const siteDescription = generalSettings?.siteDescription
    ? generalSettings.siteDescription
    : "Site Description"
  const manageConsentText = generalSettings?.manageConsentText
    ? generalSettings.manageConsentText
    : "Manage Consent Settings."
  const [menus, setMenus] = useState(() => {
    if (rawMenus) {
      return createHierarchicalMenusFromAllWpMenuNodes(
        rawMenus,
        language?.code ? language.code : null,
        wpSettings.defaultLanguage?.code
          ? wpSettings.defaultLanguage.code
          : null
      )
    }
    return []
  })

  /**
   * We should find a way to automatically switch to a boxed layout
   * if we detect a desktop skin ad available.
   */
  const [hasDesktopSkin, setHasDesktopSkin] = useState(false)

  useEffect(() => {
    if (rawMenus) {
      setMenus(
        createHierarchicalMenusFromAllWpMenuNodes(
          rawMenus,
          language?.code ? language.code : null,
          wpSettings.defaultLanguage.code
        )
      )
    }
  }, [rawMenus])
  useEffect(() => {
    // https://react-dfp.surge.sh/dfpmanager/reload/
    if (gamActive()) {
      DFPManager.load()
    }
    return () => {
      if (gamActive()) {
        DFPManager.destroyGPTSlots()
      }
    }
  }, [])

  const FooterBottom = ({ className }) => (
    <div className={className}>
      <div
        className="footer-bottom-text dry-container"
        dangerouslySetInnerHTML={{ __html: footer.footerBottom }}
      ></div>
      {gamActive() && (
        <div className="revoke-consent">
          <ManageConsent message={manageConsentText}></ManageConsent>
        </div>
      )}
      {gamActive() && !gamIsJustConsent() && (
        <div className="footer-spacer"></div>
      )}
    </div>
  )

  const StyledFooterBottom = styled(FooterBottom)`
    .revoke-consent {
      margin: 1rem 0;
      font-size: 0.9rem;
    }
    .footer-spacer {
      height: 80px;
      background-color: var(--dark);
    }

    @media (min-height: 1000px) {
      .footer-spacer {
        height: 120px;
      }
    }
  `

  const layout = (
    <div className={className}>
      {hasDesktopSkin && (
        <div className="desktop-skin">
          <DesktopSkin></DesktopSkin>
        </div>
      )}
      <div
        className={`layout-container ${
          hasDesktopSkin ? `boxed-layout dry-container` : null
        }`}
      >
        {
          <Header
            className="layout-header"
            homepageUri={homepageUri}
            location={location}
            primaryMenu={menus?.MAIN_MENU_LOCATION}
            searchSettings={searchSettings}
            searchUrls={searchUrls}
            siteDescription={siteDescription}
            siteTitle={siteTitle}
            socialNetworks={socialNetworks}
            translationsMenu={translations}
          ></Header>
        }
        {children}
        {
          <Footer
            className="layout-footer"
            footerFields={footer.footerFields}
            footerBottom={<StyledFooterBottom></StyledFooterBottom>}
            manageConsentText={manageConsentText}
          ></Footer>
        }
      </div>
    </div>
  )
  return gamActive() ? (
    <DFPSlotsProvider dfpNetworkId={String(gamNetworkCode())}>
      {layout}
      {gamIsJustConsent() ? null : (
        <StickyAdSlot adUnitName="Sticky Bottom"></StickyAdSlot>
      )}
    </DFPSlotsProvider>
  ) : (
    layout
  )
}

Layout.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  footer: PropTypes.object,
  generalSettings: PropTypes.object,
  homepageUri: PropTypes.string,
  language: PropTypes.object,
  location: PropTypes.object,
  rawMenus: PropTypes.object,
  searchSettings: PropTypes.object,
  searchUrls: PropTypes.object,
  socialNetworks: PropTypes.object,
  translations: PropTypes.array,
  wpSettings: PropTypes.object,
}

const StyledLayout = styled(Layout)`
  background-color: var(--light);
  height: 100vh;
  margin: 0;
  display: flex;
  flex-direction: column;
  padding-bottom: ${gamActive() ? "80px" : "0"};
  overflow-x: hidden;
  .desktop-skin {
    position: fixed;
    width: 100vw;
    height: 100vh;
    background-color: var(--light);
    /* background-image: linear-gradient(
      to bottom right,
      var(--light),
      var(--tertiary, rgba(255, 255, 255, 0))
    ); */
    z-index: 0;
  }

  .boxed-layout {
    z-index: 1;
    background-color: var(--light);
    padding: 2rem;
  }

  .layout-container {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .layout-header {
    position: relative;
    z-index: 30;
    width: 100%;
  }

  .layout-footer {
    margin-top: auto; // footer at bottom
  }

  /** Utility classes */
  .extend-to-screen {
    left: 50%;
    margin-left: -50vw;
    margin-right: -50vw;
    max-width: 100vw;
    position: relative;
    right: 50%;
    width: 100vw;
  }

  .overlay {
    height: 100%;
    width: 100%;
    background: rgba(0, 0, 0, 0.65);
    display: block;
  }

  .gi-overlay {
    &:after {
      content: "";
      display: block;
      position: absolute;
      background: rgba(0, 0, 0, 0.65);
      z-index: 1;
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
    }
  }

  .with-separator {
    position: relative;
    &:after {
      display: block;
      height: 3px;
      width: 50%;
      position: absolute;
      top: auto;
      bottom: 0;
      left: 0;
      right: 0;
      margin-left: auto;
      margin-right: auto;
      content: "";
    }
    &.last {
      &:after {
        display: none;
      }
    }
  }

  .btn-primary {
    padding: 1rem;
    font-size: 1.25rem;
    line-height: 1.75rem;
    font-weight: 500;
    text-transform: uppercase;
    color: var(--light);
    a {
      display: inline-block;
      padding: 1rem;
      transition-property: background-color, border-color, color, fill, stroke;
      transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
      transition-duration: 1000ms;
      cursor: pointer;
      background-color: var(--secondary);
      &:hover {
        background-color: var(--tertiary);
      }
    }
  }

  @media (min-width: ${screens.lg}) {
    /** Utility classes */
    .dry-container {
      width: 100%;
      margin-left: auto;
      margin-right: auto;
    }
  }

  @media (min-width: ${screens.xl}) {
    /** Utility classes */
    .dry-container {
      max-width: 64rem /* 1024px */;
    }
  }
`

export default StyledLayout

export { Layout }
